import AddIcon from "@mui/icons-material/Add";
import { Button, Chip } from "@mui/material";

import type { ProjectActorDto } from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";

interface ProjectActorsProps {
  actors: ProjectActorDto[];
  onAddActor?: () => void;
  isEditable?: boolean;
  isSingleVoiceSpot?: boolean;
  hasBlockingJobs?: boolean;
}

export const ProjectActors = ({
  actors,
  onAddActor,
  isEditable = true,
  isSingleVoiceSpot = false,
  hasBlockingJobs = false,
}: ProjectActorsProps): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();

  const action = onAddActor && isEditable && (
    <Button
      variant="text"
      color="primary"
      startIcon={<AddIcon />}
      onClick={onAddActor}
      disabled={isSingleVoiceSpot && hasBlockingJobs}
      sx={{ fontSize: "16px", textTransform: "none" }}
    >
      {t(Resources.Projects.ProjectDetail.AddActorToProject)}
    </Button>
  );

  const items: BlDetailSectionItem[] = actors.map((actor) => ({
    id: actor.actorID,
    startAdornment: (
      <DetailSection.AvatarAdornment
        firstName={actor.firstName}
        lastName={actor.lastName}
        src={actor.profilePictureUrl}
      />
    ),
    label: { text: `${actor.firstName} ${actor.lastName}` },
    endAdornment: actor.isExclusiveContract ? (
      <Chip
        label={t(Resources.Projects.ProjectDetail.ExclusiveContract)}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ fontSize: "11px", height: "22px" }}
      />
    ) : undefined,
    onClick: () => navigate(AppRouting.ActorDetail.path(actor.actorID)),
  }));

  return (
    <DetailSection
      title={t(
        isSingleVoiceSpot
          ? Resources.Projects.ProjectDetail.Actor
          : Resources.Projects.ProjectDetail.Actors,
      )}
      action={action || undefined}
      items={items}
    />
  );
};
